import React from "react";
import {
  FormControl,
  InputRightElement,
  InputGroup,
  Stack,
  Text,
  Input,
  Button,
  Divider,
  AbsoluteCenter,
  Box,
} from "@chakra-ui/react";
import BackButton from "@/components/Buttons/BackButton";
import ParrotText from "@/components/ParrotText";
import { useToast } from "@chakra-ui/react";
import { useAuthProviders } from "@/hooks/UseAuthProviders";
import { axiosInstance } from "@/api/Api";
import { GetUserInfoResponse } from "@shared_types";
import { OAUTH_REDIRECT_URL } from "@/api/base";
import { Navigate, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { tokenStorage } from "@/api/tokenStorage";
import { signInUser, sendPasswordResetEmail } from "@/api/login";
import { getPlatform } from "@/lib/iap";
import { SocialAuthButtons } from "@/components/SocialAuthButtons";
import { LoadingPage } from "./LoadingPage";

const PLATFORM = getPlatform();
const IS_APPLE = PLATFORM === "apple";

const LoginPage: React.FC = () => {
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [show, setShow] = React.useState(false);
  const [showPasswordReset, setShowPasswordReset] = React.useState(false);
  const redirectUrl = OAUTH_REDIRECT_URL + "/redirect";
  const userInfoQuery = useQuery({
    queryKey: ["AuthQueryLoginPage"],
    retry: 0,
    queryFn: async () => {
      const authToken = tokenStorage.getToken();
      const response = await axiosInstance.get<GetUserInfoResponse>(
        "/login/status",
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );
      return response.data;
    },
  });

  // If the user is logged out we will get id 0, hence we
  // dont want to be redirecting
  let userInfo: GetUserInfoResponse | undefined =
    userInfoQuery.data || undefined;
  if (userInfoQuery.data && userInfoQuery.data.id == 0) {
    userInfo = undefined;
  }
  const toast = useToast();
  const authProviders = useAuthProviders();
  const nav = useNavigate();
  const shouldRedirect =
    userInfo != undefined &&
    !userInfo.isTemp &&
    !userInfo.isTempTrial &&
    !userInfo.isTempPremium;
  const isPageLoading = tokenStorage.isTokenSet() && userInfoQuery.isPending;
  if (isPageLoading) return <LoadingPage />;

  if (shouldRedirect) {
    return <Navigate to="/home/learn" />;
  }

  const handleLogin = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (email.trim() === "") {
      toast({
        title: "Email required",
        description: "Please enter an email address",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    } else if (!emailRegex.test(email)) {
      toast({
        title: "Invalid email format",
        description: "Please enter a valid email address",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    } else if (password == "") {
      toast({
        title: "Password required",
        description: "Please enter you password.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    } else {
      try {
        await signInUser(email, password);
        nav("/home/learn");
      } catch (err) {
        let uiErrMsg;
        if ((err as any).response.code == 400) {
          uiErrMsg = "Please check your email and password";
        }
        if (!uiErrMsg) {
          uiErrMsg = "Sign-in failed please try again.";
        }
        toast({
          title: "Sign-in failed",
          description: `${uiErrMsg}`,
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top",
        });
      }
    }
  };

  const handlePasswordReset = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (email.trim() === "") {
      toast({
        title: "Email required",
        description: "Please enter an email address",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    } else if (!emailRegex.test(email)) {
      toast({
        title: "Invalid email format",
        description: "Please enter a valid email address",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    } else {
      try {
        await sendPasswordResetEmail(email);
        toast({
          title: "Password reset email sent",
          description:
            "If there's an account associated with this email, you will receive an email with instructions to reset your password.",
          status: "success",
          duration: 5000,
          isClosable: true,
          position: "top",
        });
        setShowPasswordReset(false);
      } catch (err) {
        console.error(err);
        toast({
          title: "Error sending password reset email",
          description: "Please try again later",
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top",
        });
      }
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
  };

  const handlePassswordChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPassword(event.target.value);
  };

  return (
    <>
      <Stack
        spacing={6}
        width={"100%"}
        height={"100%"}
        justifyContent={"space-between"}
        align={"center"}
        overflow={"hidden"}
        className="appPadding overflow-hidden"
      >
        <div className="self-start">
          <BackButton
            onBack={() => {
              nav("/");
            }}
          />
        </div>
        <Stack align="center" spacing={10} width={"100%"}>
          <ParrotText />
          <Stack align="center" spacing={0}>
            <div className="text-black text-center text-xl font-semibold mb-2 mt-2">
              Log in to your account
            </div>
            <div className="text-sm">Continue where you left off!</div>2
          </Stack>
        </Stack>

        {showPasswordReset ? (
          <Stack
            align="center"
            spacing={6}
            width={"100%"}
            bg={"white"}
            padding={4}
            paddingY={6}
            rounded={"2xl"}
          >
            <Input
              value={email}
              onChange={handleInputChange}
              fontSize="var(--base-font-size)"
              placeholder="Email"
            />
            <Button
              type="button"
              background={"var(--color-blue)"}
              textColor={"white"}
              onClick={handlePasswordReset}
              fontSize="var(--base-font-size)"
            >
              Reset Password
            </Button>
          </Stack>
        ) : (
          <Stack
            align="center"
            spacing={6}
            width={"100%"}
            bg={"white"}
            padding={4}
            paddingY={6}
            rounded={"2xl"}
          >
            <Stack spacing={4} width={"100%"}>
              <Input
                value={email}
                onChange={handleInputChange}
                placeholder="Email"
                fontSize="var(--base-font-size-small)"
              />
              <FormControl isRequired>
                <InputGroup size="md">
                  <Input
                    value={password}
                    type={show ? "text" : "password"}
                    isRequired
                    placeholder="Password"
                    onChange={handlePassswordChange}
                    fontSize="var(--base-font-size-small)"
                  />
                  <InputRightElement width="4.5rem">
                    <Button
                      h="1.75rem"
                      size="sm"
                      onClick={() => setShow(!show)}
                      fontSize="var(--base-font-size-xs)"
                    >
                      {show ? "Hide" : "Show"}
                    </Button>
                  </InputRightElement>
                </InputGroup>
              </FormControl>

              <Text className="text-sm" fontSize="var(--base-font-size-small)">
                Forgot your password?{" "}
                <Button
                  variant="link"
                  fontWeight={"normal"}
                  onClick={() => setShowPasswordReset(true)}
                  color="var(--color-blue)"
                  fontSize="var(--base-font-size-small)"
                >
                  Reset here
                </Button>
              </Text>

              <Button
                type="button"
                background={"var(--color-blue)"}
                textColor={"white"}
                onClick={handleLogin}
                fontSize="var(--base-font-size)"
              >
                Login
              </Button>
            </Stack>

            <Box width={"100%"} position={"relative"}>
              <Divider />
              <AbsoluteCenter
                bg="white"
                px="4"
                textColor={"gray.500"}
                fontSize="var(--base-font-size-small)"
              >
                OR
              </AbsoluteCenter>
            </Box>
            <SocialAuthButtons
              authProviders={authProviders}
              redirectUrl={redirectUrl}
              isApplePlatform={IS_APPLE}
              userID={userInfo?.id || 0}
              // isTempFlag determines if the user came from onboarding already
              // since login page is impossible to reach if they have already
              // completed onboarding, we can just set this to false
              isTemp={false}
            />
            <div className="text-xs text-gray-500">
              <a onClick={() => nav("/privacy")}>Privacy Policy</a>
              <span className="mx-2">|</span>
              <a onClick={() => nav("/terms")}>Terms of Service</a>
              <span className="mx-2">|</span>
              <a onClick={() => nav("/support")}>Support</a>
            </div>
          </Stack>
        )}
        <div className="w-full min-h-10"></div>
      </Stack>
    </>
  );
};

export default LoginPage;
